export const CAMPAIGN_TABLE_GRID =
  "grid-cols-[64px_360px_90px_minmax(140px,1.2fr)_156px_126px_90px_minmax(150px,1.25fr)_120px_repeat(4,110px)_repeat(10,minmax(82px,1fr))_minmax(132px,1.35fr)_128px_22px] pl-0";

export const CAMPAIGN_TABLE_MIN_WIDTH = "min-w-[2882px]";
export const CAMPAIGN_SWITCH_STICKY_POSITION = "sticky left-0";
export const CAMPAIGN_NAME_STICKY_POSITION = "sticky left-16";
export const CAMPAIGN_TABLE_CELL_INSET = "px-3.5";

export const CAMPAIGN_TABLE_COLUMNS = [
  { id: "switch", defaultWidth: 64, minWidth: 56, maxWidth: 120 },
  { id: "campaign", defaultWidth: 360, minWidth: 220, maxWidth: 720 },
  { id: "delivery", defaultWidth: 90, minWidth: 88, maxWidth: 180 },
  { id: "shop", defaultWidth: 140, minWidth: 100, maxWidth: 360 },
  { id: "managedGroup", defaultWidth: 156, minWidth: 120, maxWidth: 320 },
  { id: "aiPolicy", defaultWidth: 126, minWidth: 96, maxWidth: 240 },
  { id: "targetingType", defaultWidth: 90, minWidth: 80, maxWidth: 220 },
  { id: "biddingStrategy", defaultWidth: 150, minWidth: 120, maxWidth: 360 },
  { id: "startDate", defaultWidth: 120, minWidth: 100, maxWidth: 220 },
  { id: "dailyBudget", defaultWidth: 110, minWidth: 88, maxWidth: 240 },
  { id: "currentBudget", defaultWidth: 110, minWidth: 88, maxWidth: 240 },
  { id: "clickCost", defaultWidth: 110, minWidth: 88, maxWidth: 220 },
  { id: "orderCost", defaultWidth: 110, minWidth: 88, maxWidth: 220 },
  { id: "ctr", defaultWidth: 82, minWidth: 72, maxWidth: 180 },
  { id: "cvr", defaultWidth: 82, minWidth: 72, maxWidth: 180 },
  { id: "acos", defaultWidth: 82, minWidth: 72, maxWidth: 180 },
  { id: "roas", defaultWidth: 82, minWidth: 72, maxWidth: 180 },
  { id: "spend", defaultWidth: 82, minWidth: 72, maxWidth: 240 },
  { id: "impressions", defaultWidth: 82, minWidth: 72, maxWidth: 240 },
  { id: "clicks", defaultWidth: 82, minWidth: 72, maxWidth: 220 },
  { id: "orders", defaultWidth: 82, minWidth: 72, maxWidth: 220 },
  { id: "units", defaultWidth: 82, minWidth: 72, maxWidth: 220 },
  { id: "sales", defaultWidth: 82, minWidth: 72, maxWidth: 240 },
  { id: "advertisedProductOrders", defaultWidth: 132, minWidth: 120, maxWidth: 320 },
  { id: "latestRun", defaultWidth: 128, minWidth: 100, maxWidth: 280 },
] as const;

export type CampaignTableColumnId = (typeof CAMPAIGN_TABLE_COLUMNS)[number]["id"];
export type CampaignTableColumnWidths = Record<CampaignTableColumnId, number>;

export const CAMPAIGN_TABLE_FIXED_COLUMN_IDS = [
  "switch",
  "campaign",
] as const satisfies readonly CampaignTableColumnId[];
export const CAMPAIGN_TABLE_NON_FIXED_COLUMN_IDS = CAMPAIGN_TABLE_COLUMNS.map(
  (column) => column.id,
).filter(
  (columnId): columnId is Exclude<CampaignTableColumnId, "switch" | "campaign"> =>
    !CAMPAIGN_TABLE_FIXED_COLUMN_IDS.includes(
      columnId as (typeof CAMPAIGN_TABLE_FIXED_COLUMN_IDS)[number],
    ),
);

const CAMPAIGN_TABLE_ACTION_WIDTH = 22;

export function getCampaignTableDefaultWidths(): CampaignTableColumnWidths {
  return Object.fromEntries(
    CAMPAIGN_TABLE_COLUMNS.map((column) => [column.id, column.defaultWidth]),
  ) as CampaignTableColumnWidths;
}

export function clampCampaignTableColumnWidth(
  columnId: CampaignTableColumnId,
  width: number,
): number {
  const column = CAMPAIGN_TABLE_COLUMNS.find((candidate) => candidate.id === columnId);
  if (!column) return width;
  return Math.min(column.maxWidth, Math.max(column.minWidth, Math.round(width)));
}

export function getCampaignTableGridTemplate(
  widths: CampaignTableColumnWidths,
  columnIds: readonly CampaignTableColumnId[] = CAMPAIGN_TABLE_COLUMNS.map((column) => column.id),
): string {
  return `${columnIds.map((columnId) => `${widths[columnId]}px`).join(" ")} minmax(${CAMPAIGN_TABLE_ACTION_WIDTH}px, 1fr)`;
}

export function getCampaignTableWidth(
  widths: CampaignTableColumnWidths,
  columnIds: readonly CampaignTableColumnId[] = CAMPAIGN_TABLE_COLUMNS.map((column) => column.id),
): number {
  return (
    columnIds.reduce((total, columnId) => total + widths[columnId], 0) + CAMPAIGN_TABLE_ACTION_WIDTH
  );
}
